import React, { useState, useEffect } from 'react';
import { GitBranch, ExternalLink, FileText, Calendar, AlertCircle, GripVertical, ArrowUp, ArrowDown } from 'lucide-react';
import { DeliveryItemFormData, DeliveryStatus } from '../../types/delivery.types';
import { AvailableProject } from '../../types/delivery.types';
import Input from '../ui/Input';
import Button from '../ui/Button';
import RichTextEditor from '../ui/RichTextEditor';
import { useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { DeliveryAttachmentList } from './DeliveryAttachmentList';

interface DeliveryItemFormProps {
    project: AvailableProject;
    initialData?: DeliveryItemFormData;
    onSave: (data: DeliveryItemFormData) => void;
    onCancel?: () => void;
    isReadOnly?: boolean;
    customActions?: React.ReactNode;
    position?: number;
    isFirst?: boolean;
    isLast?: boolean;
    onMoveUp?: () => void;
    onMoveDown?: () => void;
    dragHandleProps?: Record<string, any>;
}

const deliveryItemSchema = yup.object({
    id: yup.number().optional(),
    deliveryId: yup.number().optional(),
    projectId: yup.number().required('Projeto é obrigatório'),
    projectName: yup.string().optional(),
    status: yup.string().required('Status é obrigatório'),
    branch: yup.string().optional(),
    sourceBranch: yup.string().optional(),
    pullRequest: yup.string().url('Deve ser uma URL válida').optional(),
    startedAt: yup.string().required('Data de início é obrigatória'),
    finishedAt: yup.string().when('status', {
        is: 'DELIVERED',
        then: (schema) => schema.required('Data de conclusão é obrigatória para status "Entregue"'),
        otherwise: (schema) => schema.optional()
    }),
    notes: yup.string().optional()
});

const statusOptions: { value: DeliveryStatus; label: string; color: string; bg: string }[] = [
    { value: 'PENDING', label: 'Pendente', color: 'text-yellow-700', bg: 'bg-yellow-50 border-yellow-100' },
    { value: 'DEVELOPMENT', label: 'Desenvolvimento', color: 'text-accent', bg: 'bg-info-soft border-blue-100' },
    { value: 'DELIVERED', label: 'Entregue', color: 'text-green-700', bg: 'bg-green-50 border-green-100' },
    { value: 'HOMOLOGATION', label: 'Homologação', color: 'text-amber-700', bg: 'bg-amber-50 border-amber-100' },
    { value: 'APPROVED', label: 'Aprovado', color: 'text-emerald-700', bg: 'bg-emerald-50 border-emerald-100' },
    { value: 'REJECTED', label: 'Rejeitado', color: 'text-rose-700', bg: 'bg-rose-50 border-rose-100' },
    { value: 'PRODUCTION', label: 'Produção', color: 'text-violet-700', bg: 'bg-violet-50 border-violet-100' },
    { value: 'CANCELLED', label: 'Cancelado', color: 'text-red-600', bg: 'bg-red-50 border-red-200' }
];

export default function DeliveryItemForm({
    project,
    initialData,
    onSave,
    onCancel,
    isReadOnly = false,
    customActions,
    position,
    isFirst,
    isLast,
    onMoveUp,
    onMoveDown,
    dragHandleProps
}: DeliveryItemFormProps) {
    const [isExpanded, setIsExpanded] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

    const {
        register,
        handleSubmit,
        watch,
        setValue,
        reset,
        control,
        formState: { errors, isDirty }
    } = useForm<DeliveryItemFormData>({
        resolver: yupResolver(deliveryItemSchema),
        defaultValues: {
            projectId: project.id,
            projectName: project.name,
            status: 'PENDING',
            branch: '',
            sourceBranch: '',
            pullRequest: '',
            startedAt: '',
            finishedAt: '',
            notes: '',
            ...initialData
        }
    });

    const currentStatus = watch('status');

    useEffect(() => {
        setHasUnsavedChanges(isDirty && !isReadOnly);
    }, [isDirty, isReadOnly]);

    useEffect(() => {
        if (initialData) {
            reset({
                ...initialData,
                projectId: project.id,
                projectName: project.name,
                startedAt: initialData.startedAt ? initialData.startedAt.substring(0, 16) : '',
                finishedAt: initialData.finishedAt ? initialData.finishedAt.substring(0, 16) : ''
            });
        }
    }, [initialData, project, reset]);

    const onSubmit = (data: DeliveryItemFormData) => {
        onSave({
            ...data,
            id: initialData?.id,
            deliveryId: initialData?.deliveryId,
            projectId: project.id,
            projectName: project.name
        });
        setHasUnsavedChanges(false);
    };

    const handleCancel = () => {
        if (hasUnsavedChanges) {
            const confirmCancel = window.confirm(
                'Há alterações não salvas. Deseja realmente cancelar?'
            );
            if (!confirmCancel) return;
        }
        
        if (onCancel) {
            onCancel();
        }
        reset();
        setHasUnsavedChanges(false);
    };

    const getCurrentStatusInfo = () => {
        return statusOptions.find(option => option.value === currentStatus) || statusOptions[0];
    };

    const statusInfo = getCurrentStatusInfo();

    return (
        <div className="min-w-0 border border-border-subtle rounded-lg overflow-hidden">
            {/* Header */}
            <div 
                className={`p-3 sm:p-4 bg-surface-app cursor-pointer hover:bg-surface-2 transition-colors ${
                    hasUnsavedChanges ? 'border-l-4 border-l-yellow-400' : ''
                }`}
                onClick={() => !isReadOnly && setIsExpanded(!isExpanded)}
            >
                {/* Mobile Layout */}
                <div className="block sm:hidden">
                    <div className="space-y-3">
                        {/* Nome e Link */}
                        <div className="flex items-start justify-between">
                            <div className="flex items-start gap-2 flex-1 min-w-0">
                                {dragHandleProps && (
                                    <button
                                        type="button"
                                        {...dragHandleProps}
                                        onClick={(e) => e.stopPropagation()}
                                        title="Arraste para reordenar"
                                        className="p-1 -m-1 rounded hover:bg-surface-3 text-text-tertiary cursor-grab active:cursor-grabbing touch-none flex-shrink-0"
                                    >
                                        <GripVertical className="h-4 w-4" />
                                    </button>
                                )}
                                {position !== undefined && (
                                    <span
                                        className="inline-flex items-center justify-center mt-0.5 px-2 py-0.5 rounded-lg text-xs font-bold bg-gradient-to-r from-blue-500 to-purple-600 text-white flex-shrink-0"
                                        title={initialData?.id ? `ID: ${initialData.id}` : ''}
                                    >
                                        {position}
                                    </span>
                                )}
                                <GitBranch className="h-4 w-4 text-text-tertiary mt-1 flex-shrink-0" />
                                <h3 className="font-medium text-text-primary text-sm leading-5 break-words">
                                    {project.name}
                                </h3>
                            </div>
                            {project.repositoryUrl && (
                                <a 
                                    href={project.repositoryUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-accent hover:text-info-strong ml-2 flex-shrink-0"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <ExternalLink className="h-4 w-4" />
                                </a>
                            )}
                        </div>
                        
                        {/* Status e Ações */}
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${statusInfo.color} ${statusInfo.bg}`}>
                                    ✓ {statusInfo.label}
                                </span>
                                {hasUnsavedChanges && (
                                    <span className="inline-flex items-center gap-1 text-yellow-600 text-xs">
                                        <AlertCircle className="h-3 w-3" />
                                        Não salvo
                                    </span>
                                )}
                            </div>
                            
                            <div className="flex items-center gap-1">
                                {onMoveUp && (
                                    <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); onMoveUp(); }}
                                        disabled={isFirst}
                                        title="Mover para cima"
                                        className="p-1 rounded hover:bg-surface-3 disabled:opacity-40 disabled:cursor-not-allowed text-text-secondary"
                                    >
                                        <ArrowUp className="h-4 w-4" />
                                    </button>
                                )}
                                {onMoveDown && (
                                    <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); onMoveDown(); }}
                                        disabled={isLast}
                                        title="Mover para baixo"
                                        className="p-1 rounded hover:bg-surface-3 disabled:opacity-40 disabled:cursor-not-allowed text-text-secondary"
                                    >
                                        <ArrowDown className="h-4 w-4" />
                                    </button>
                                )}
                                {customActions && (
                                    <div onClick={(e) => e.stopPropagation()}>
                                        {customActions}
                                    </div>
                                )}
                                {!isReadOnly && (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setIsExpanded(!isExpanded);
                                        }}
                                        className="px-2 py-1"
                                    >
                                        {isExpanded ? '⌃' : '⌄'}
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Desktop Layout - Original */}
                <div className="hidden sm:flex items-center justify-between">
                    <div className="flex items-center gap-3 min-w-0">
                        {dragHandleProps && (
                            <button
                                type="button"
                                {...dragHandleProps}
                                onClick={(e) => e.stopPropagation()}
                                title="Arraste para reordenar"
                                className="p-1 -m-1 rounded hover:bg-surface-3 text-text-tertiary cursor-grab active:cursor-grabbing touch-none flex-shrink-0"
                            >
                                <GripVertical className="h-4 w-4" />
                            </button>
                        )}
                        {position !== undefined && (
                            <span
                                className="inline-flex items-center justify-center px-2 py-0.5 rounded-lg text-xs font-bold bg-gradient-to-r from-blue-500 to-purple-600 text-white flex-shrink-0"
                                title={initialData?.id ? `ID: ${initialData.id}` : ''}
                            >
                                {position}
                            </span>
                        )}
                        <div className="flex items-center gap-2 min-w-0">
                            <GitBranch className="h-4 w-4 text-text-tertiary flex-shrink-0" />
                            <h3 className="font-medium text-text-primary truncate">
                                {project.name}
                            </h3>
                        </div>
                        
                        {project.repositoryUrl && (
                            <a 
                                href={project.repositoryUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-accent hover:text-info-strong"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <ExternalLink className="h-4 w-4" />
                            </a>
                        )}
                    </div>
                    
                    <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${statusInfo.color} ${statusInfo.bg}`}>
                            {statusInfo.label}
                        </span>

                        {hasUnsavedChanges && (
                            <div className="flex items-center gap-1 text-yellow-600">
                                <AlertCircle className="h-4 w-4" />
                                <span className="text-xs font-medium">Não salvo</span>
                            </div>
                        )}

                        {onMoveUp && (
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); onMoveUp(); }}
                                disabled={isFirst}
                                title="Mover para cima"
                                className="p-1 rounded hover:bg-surface-3 disabled:opacity-40 disabled:cursor-not-allowed text-text-secondary"
                            >
                                <ArrowUp className="h-4 w-4" />
                            </button>
                        )}
                        {onMoveDown && (
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); onMoveDown(); }}
                                disabled={isLast}
                                title="Mover para baixo"
                                className="p-1 rounded hover:bg-surface-3 disabled:opacity-40 disabled:cursor-not-allowed text-text-secondary"
                            >
                                <ArrowDown className="h-4 w-4" />
                            </button>
                        )}

                        {customActions && (
                            <div onClick={(e) => e.stopPropagation()}>
                                {customActions}
                            </div>
                        )}

                        {!isReadOnly && (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setIsExpanded(!isExpanded);
                                }}
                            >
                                {isExpanded ? '−' : '+'}
                            </Button>
                        )}
                    </div>
                </div>
            </div>

            {/* Formulário expandido */}
            {isExpanded && (
                <form onSubmit={handleSubmit(onSubmit)} className="p-3 sm:p-4 space-y-4 min-w-0">
                    {/* Status */}
                    <div>
                        <label className="block text-sm font-medium text-text-secondary mb-1">
                            Status *
                        </label>
                        <select
                            {...register('status')}
                            disabled={isReadOnly}
                            className="w-full p-2 border border-border-strong rounded-md focus:ring-2 focus:ring-accent focus:border-accent disabled:bg-surface-2"
                        >
                            {statusOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                        {errors.status && (
                            <p className="mt-1 text-sm text-red-600">{errors.status.message}</p>
                        )}
                    </div>

                    {/* Branches */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-text-secondary mb-1">
                                Branch de Desenvolvimento
                            </label>
                            <Input
                                {...register('branch')}
                                placeholder="feature/nova-funcionalidade"
                                disabled={isReadOnly}
                                icon={GitBranch}
                            />
                        </div>
                        
                        <div>
                            <label className="block text-sm font-medium text-text-secondary mb-1">
                                Branch de Origem
                            </label>
                            <Input
                                {...register('sourceBranch')}
                                placeholder="develop, main..."
                                disabled={isReadOnly}
                                icon={GitBranch}
                            />
                        </div>
                    </div>

                    {/* Pull Request */}
                    <div>
                        <label className="block text-sm font-medium text-text-secondary mb-1">
                            Pull Request / Merge Request
                        </label>
                        <Input
                            {...register('pullRequest')}
                            placeholder="https://github.com/repo/pull/123"
                            disabled={isReadOnly}
                            icon={ExternalLink}
                        />
                        {errors.pullRequest && (
                            <p className="mt-1 text-sm text-red-600">{errors.pullRequest.message}</p>
                        )}
                    </div>

                    {/* Datas */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-text-secondary mb-1">
                                Data de Início *
                            </label>
                            <Input
                                {...register('startedAt')}
                                type="datetime-local"
                                disabled={isReadOnly}
                                icon={Calendar}
                            />
                            {errors.startedAt && (
                                <p className="mt-1 text-sm text-red-600">{errors.startedAt.message}</p>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-text-secondary mb-1">
                                Data de Conclusão
                            </label>
                            <Input
                                {...register('finishedAt')}
                                type="datetime-local"
                                disabled={isReadOnly}
                                icon={Calendar}
                            />
                            {errors.finishedAt && (
                                <p className="mt-1 text-sm text-red-600">{errors.finishedAt.message}</p>
                            )}
                        </div>
                    </div>

                    {/* Observações */}
                    <Controller
                        name="notes"
                        control={control}
                        render={({ field }) => (
                            <RichTextEditor
                                label="Observações"
                                value={field.value || ''}
                                onChange={field.onChange}
                                placeholder="Descreva as observações sobre a implementação em detalhes. Você pode colar imagens diretamente..."
                                minHeight="200px"
                                disabled={isReadOnly}
                                entityType="DELIVERY_DEVELOPMENT_ITEM"
                                entityId={initialData?.id}
                                parentId={initialData?.deliveryId}
                            />
                        )}
                    />


                    {/* Anexos do Item */}
                    {initialData?.id && (
                        <DeliveryAttachmentList
                            deliveryItemId={initialData.id}
                            readOnly={isReadOnly}
                            className="border-t pt-4 mt-6"
                        />
                    )}

                    {/* Ações */}
                    {!isReadOnly && (
                        <div className="flex flex-wrap items-center gap-3 pt-4">
                            <Button
                                type="submit"
                                disabled={!isDirty}
                            >
                                Salvar Alterações
                            </Button>
                            
                            {onCancel && (
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={handleCancel}
                                >
                                    Cancelar
                                </Button>
                            )}
                            
                            {isDirty && (
                                <span className="text-sm text-text-tertiary">
                                    Há alterações não salvas
                                </span>
                            )}
                        </div>
                    )}
                </form>
            )}
        </div>
    );
}